import React, { useCallback, useEffect, useState } from "react";
import { Download } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { InvoiceBadge, INV_LABEL } from "./InvoiceMark";
import { toast } from "sonner";

const TYPE = { invoice: "Invoice", uchanti: "Uchanti", company_vehicle: "Company vehicle", foc: "FOC", no_work: "No work done", outside_work: "Outside work", employee: "Employee", trial: "Trial" };
const COLS = "grid grid-cols-2 md:grid-cols-[110px_120px_minmax(0,1fr)_minmax(0,1fr)_110px_110px_100px_110px] gap-2 md:gap-4 px-4 md:px-5";
const time = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const dur = (from, to) => {
  const m = Math.max(0, Math.round((new Date(to) - new Date(from)) / 60000));
  const d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60), mm = m % 60;
  return d ? `${d}d ${h}h` : h ? `${h}h ${mm}m` : `${mm}m`;
};
const isOut = (g) => g.status === "out";
const localToday = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const insideFor = (g, dayEnd) => dur(g.created_at, isOut(g, dayEnd) ? g.released_at : (new Date(dayEnd) < new Date() ? dayEnd : new Date().toISOString()));

const exportCsv = (data) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [["Gate pass", "Type", "Vehicle", "Driver", "Driver mobile", "Account / party", "Job card", "Payment", "Invoice status", "Invoice no", "Invoice remarks", "Marked by", "Issued", "Issued by", "Out", "Time inside", "Status"].join(","),
    ...data.rows.map((g) => [g.number, TYPE[g.gp_type], g.vehicle_number, g.driver_name, g.driver_mobile, g.party_name, g.job_card_number, g.payment_type,
      INV_LABEL[g.invoice_status || (g.invoice_no ? "invoiced" : "pending")], g.invoice_no, g.invoice_remarks, g.invoice_marked_by, time(g.created_at), g.created_by_name,
      isOut(g, data.day_end) ? time(g.released_at) : "", insideFor(g, data.day_end), isOut(g, data.day_end) ? "Out" : "Inside"].map(esc).join(","))];
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" }));
  a.download = `gate_report_${data.date}.csv`; a.click(); URL.revokeObjectURL(a.href);
};

const Stat = ({ title, value, tone, testid }) => (
  <div className={`rounded-lg border px-4 py-3 ${tone}`}><div className="text-xs text-muted-foreground">{title}</div><div data-testid={testid} className="num text-xl font-bold">{value}</div></div>
);

const ReportRow = ({ g, dayEnd }) => {
  const out = isOut(g, dayEnd);
  return (
    <div data-testid={`gpr-row-${g.id}`} className={`${COLS} py-3 items-center text-sm`}>
      <span className="num font-bold text-primary">{g.number}</span>
      <span className="num font-semibold">{g.vehicle_number}</span>
      <div className="min-w-0 col-span-2 md:col-span-1"><div className="font-medium break-words">{g.driver_name}</div><div className="num text-xs text-muted-foreground">{g.driver_mobile}</div></div>
      <div className="min-w-0 col-span-2 md:col-span-1"><div className="break-words">{g.party_name || g.remarks || "—"}</div><div className="text-xs text-muted-foreground">{TYPE[g.gp_type]}{g.job_card_number ? ` · JC ${g.job_card_number}` : ""}{g.to_location ? ` · → ${g.to_location}` : ""}</div><div className="mt-0.5"><InvoiceBadge g={g} /></div>{g.invoice_remarks ? <div className="text-[11px] text-muted-foreground break-words">{g.invoice_remarks}</div> : null}</div>
      <span className="num text-xs">{time(g.created_at)}<span className="block font-sans text-[11px] text-muted-foreground">By {g.created_by_name || "—"}</span></span>
      <span className="num text-xs">{out ? time(g.released_at) : "—"}</span>
      <span data-testid={`gpr-inside-${g.id}`} className="num text-xs font-semibold">{insideFor(g, dayEnd)}</span>
      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full w-fit ${out ? "bg-[hsl(var(--positive))]/15 text-[hsl(var(--positive))]" : "bg-accent/15 text-accent"}`}>{out ? "OUT" : "INSIDE"}</span>
    </div>
  );
};

export const GatePassReport = () => {
  const [date, setDate] = useState(localToday());
  const [show, setShow] = useState("all");
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData((await api.get("/gatepass/report", { params: { date } })).data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [date]);
  useEffect(() => { load(); }, [load]);
  const rows = (data?.rows || []).filter((g) => show === "all" || (show === "out") === !!isOut(g, data.day_end));

  return (
    <section data-testid="gp-report">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <input data-testid="gpr-date" type="date" className="h-10 px-2 rounded-md border border-input bg-card text-sm num" value={date} onChange={(e) => setDate(e.target.value)} />
        <select data-testid="gpr-show" className="h-10 px-2 rounded-md border border-input bg-card text-sm" value={show} onChange={(e) => setShow(e.target.value)}>
          <option value="all">All vehicles</option><option value="out">Went out</option><option value="inside">Still inside</option>
        </select>
        <button data-testid="gpr-export" disabled={!data} onClick={() => exportCsv(data)} className="h-10 px-3 rounded-md border border-input bg-card text-sm flex items-center gap-1.5 hover:bg-muted transition-colors"><Download size={14} /> Download CSV</button>
      </div>
      {data && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <Stat title="Issued this day" value={data.issued} tone="border-border bg-card" testid="gpr-issued" />
          <Stat title="Went out" value={data.out} tone="border-[hsl(var(--positive))]/20 bg-[hsl(var(--positive))]/5" testid="gpr-out" />
          <Stat title="Still inside" value={data.inside} tone="border-accent/20 bg-accent/5" testid="gpr-inside" />
        </div>
      )}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className={`${COLS} hidden md:grid py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground`}>
          <span>Gate pass</span><span>Vehicle</span><span>Driver</span><span>Account / job card</span><span>Issued</span><span>Out</span><span>Time inside</span><span>Status</span>
        </div>
        {rows.length === 0 ? <div className="p-10 text-center text-sm text-muted-foreground">No vehicles for this day.</div>
          : <div className="divide-y divide-border">{rows.map((g) => <ReportRow key={g.id} g={g} dayEnd={data.day_end} />)}</div>}
      </div>
    </section>
  );
};
